import React, { useEffect, useState, useCallback } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import {
  EnvelopeSimple, CheckCircle, WarningCircle, XCircle, MinusCircle,
  ArrowClockwise, MagnifyingGlass,
} from "@phosphor-icons/react";

const STATUS_STYLES = {
  sent:    { color: "text-primary bg-primary/10 border-primary/30",           icon: CheckCircle,   label: "Envoyé" },
  skipped: { color: "text-muted-foreground bg-muted border-border",           icon: MinusCircle,   label: "Skip"   },
  invalid: { color: "text-[#8a6a14] bg-[#E0B043]/15 border-[#E0B043]/40",     icon: WarningCircle, label: "Invalide" },
  error:   { color: "text-destructive bg-destructive/10 border-destructive/30", icon: XCircle,     label: "Erreur" },
};

export default function EmailDiagPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/telepro/emails/recent", { params: { limit: 200 } });
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur chargement");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="text-sm text-muted-foreground py-6 text-center">Chargement…</div>;

  const items = (data.items || []).filter((e) => {
    if (statusFilter !== "all" && e.status !== statusFilter) return false;
    if (!q) return true;
    const qq = q.toLowerCase();
    return (e.to || "").toLowerCase().includes(qq)
        || (e.subject || "").toLowerCase().includes(qq)
        || (e.resend_id || "").toLowerCase().includes(qq);
  });

  const c = data.counts_24h || {};
  const sentPct = c.sent + c.error + c.skipped + c.invalid > 0
    ? Math.round((c.sent / (c.sent + c.error + c.skipped + c.invalid)) * 100)
    : 100;

  return (
    <div data-testid="admin-email-diag">
      <div className="bg-card border border-border rounded-sm p-4 mb-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="label-uppercase text-primary flex items-center gap-1.5 mb-1">
              <EnvelopeSimple size={14} weight="fill"/> Diagnostic — envois Resend
            </div>
            <h2 className="font-display text-lg font-medium">Journal des emails</h2>
            <p className="text-xs text-muted-foreground mt-1 max-w-2xl leading-relaxed">
              Historique des 200 derniers envois transactionnels sortis du serveur (bienvenue télépro, magic-links, notifications admin, listes d&apos;appels…).
              Si une ligne apparaît en <b>Envoyé</b>, l&apos;email est bien parti côté Resend — si l&apos;utilisateur ne l&apos;a pas reçu, vérifier son dossier spam.
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center">
            {["sent", "skipped", "invalid", "error"].map((s) => {
              const st = STATUS_STYLES[s];
              const Ico = st.icon;
              return (
                <div key={s} className={`border rounded-sm px-3 py-1.5 ${st.color}`} data-testid={`email-diag-count-${s}`}>
                  <div className="text-[9px] uppercase tracking-widest font-bold flex items-center justify-center gap-1">
                    <Ico size={10} weight="bold"/> {st.label}
                  </div>
                  <div className="font-display text-lg tabular-nums font-medium">{c[s] || 0}</div>
                </div>
              );
            })}
          </div>
        </div>
        <div className="mt-3 flex items-center justify-between text-[11px] text-muted-foreground">
          <div>Taux d&apos;envoi 24h : <b className={sentPct >= 90 ? "text-primary" : "text-destructive"}>{sentPct}%</b></div>
          <button onClick={load} disabled={loading} className="text-primary hover:underline inline-flex items-center gap-1" data-testid="email-diag-refresh">
            <ArrowClockwise size={10} weight="bold" className={loading ? "animate-spin" : ""}/> Rafraîchir
          </button>
        </div>
      </div>

      <div className="flex gap-2 mb-3 flex-wrap items-center" data-testid="email-diag-filters">
        <div className="relative flex-1 min-w-[220px]">
          <MagnifyingGlass size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" weight="bold"/>
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Rechercher email / sujet / id Resend…"
            className="w-full bg-card border border-border rounded-sm pl-7 pr-3 py-1.5 text-xs"
            data-testid="email-diag-search"
          />
        </div>
        <div className="flex gap-1">
          {["all", "sent", "skipped", "invalid", "error"].map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`text-[11px] font-semibold border rounded-sm px-2.5 py-1 transition ${
                statusFilter === s
                  ? "bg-foreground text-background border-foreground"
                  : "bg-card border-border hover:border-foreground"
              }`}
              data-testid={`email-diag-status-${s}`}
            >
              {s === "all" ? "Tous" : (STATUS_STYLES[s]?.label || s)}
            </button>
          ))}
        </div>
      </div>

      {items.length === 0 ? (
        <div className="bg-accent/20 border border-dashed border-border rounded-sm p-8 text-center text-sm text-muted-foreground" data-testid="email-diag-empty">
          Aucun email dans le journal pour ce filtre.
        </div>
      ) : (
        <div className="bg-card border border-border rounded-sm overflow-hidden">
          <table className="w-full text-xs">
            <thead className="text-left text-[10px] uppercase tracking-widest text-muted-foreground border-b border-border bg-accent/20">
              <tr>
                <th className="px-3 py-2 w-40">Date</th>
                <th className="px-3 py-2 w-24 text-center">Statut</th>
                <th className="px-3 py-2 w-56">Destinataire</th>
                <th className="px-3 py-2">Sujet</th>
                <th className="px-3 py-2 w-32">Resend ID</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {items.map((e, i) => {
                const st = STATUS_STYLES[e.status] || STATUS_STYLES.error;
                const Ico = st.icon;
                return (
                  <tr key={i} data-testid={`email-diag-row-${i}`}>
                    <td className="px-3 py-2 font-mono text-[10px] text-muted-foreground">
                      {e.created_at ? new Date(e.created_at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "medium" }) : "—"}
                    </td>
                    <td className="px-3 py-2 text-center">
                      <span className={`inline-flex items-center gap-1 border rounded-sm px-1.5 py-0.5 text-[10px] font-semibold ${st.color}`}>
                        <Ico size={10} weight="bold"/> {st.label}
                      </span>
                    </td>
                    <td className="px-3 py-2 font-mono truncate max-w-[200px]" title={e.to}>{e.to}</td>
                    <td className="px-3 py-2 truncate max-w-[380px]" title={e.subject}>
                      {e.subject}
                      {e.error && <div className="text-[10px] text-destructive italic mt-0.5">{e.error}</div>}
                    </td>
                    <td className="px-3 py-2 font-mono text-[9px] text-muted-foreground truncate max-w-[110px]" title={e.resend_id || ""}>
                      {e.resend_id ? e.resend_id.slice(0, 8) + "…" : "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
